import { useEffect, useState, useRef } from "react";
import { getImageUrl } from '../firebase/storageService';
import { authFetch } from "../utils/authFetch";


const EscalaSessao = () => {

  const [atendimentos, setAtendimentos] = useState([]);
  const [atendimentosCompletos, setAtendimentosCompletos] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortController = useRef(null);

  // Função auxiliar para filtrar atendimentos que estão acontecendo agora
  const filtrarAtendimentosAgora = (atendimentosList) => {
    const agora = new Date();
    return atendimentosList.filter((a) => {
      if (!a.startDatetime || !a.endDatetime) return false;
      const inicio = new Date(a.startDatetime);
      const fim = new Date(a.endDatetime);
      return agora >= inicio && agora <= fim;
    });
  };

  useEffect(() => {
    // Create a new AbortController for this cycle
    abortController.current = new AbortController();

    const fetchAtendimentos = async () => {
      try {
        setLoading(true);
        const hoje = new Date();
        hoje.setHours(hoje.getHours() - 3); // Subtrai 3 horas
        const dataFormatada = hoje.toISOString().split('T')[0];
        
        // Buscar todos os appointments do dia (sem filtrar por appointmentType no endpoint)
        let todosAtendimentos = [];
        
        try {
          const response = await authFetch(`appointment?date=${dataFormatada}`, {
            signal: abortController.current.signal
          });
          if (response.ok) {
            todosAtendimentos = await response.json();
          }
        } catch (err) {
          console.error("Erro ao buscar atendimentos:", err);
        }

        if (todosAtendimentos.length === 0) {
          setAtendimentos([]);
          setAtendimentosCompletos([]);
          setLoading(false);
          return;
        }

        // Separar sessões (appointmentType=1) e eventos livres (appointmentType=0)
        const sessoesData = todosAtendimentos.filter(a => a.appointmentType === 1 || a.appointmentType === null || a.appointmentType === undefined);
        const eventosLivresData = todosAtendimentos.filter(a => a.appointmentType === 0);

        // Adicionar imagens apenas para sessões (que têm paciente e terapeuta)
        const sessoesComImagem = await Promise.all(
          sessoesData.map(async (s) => {
            try {
              const urlPaciente = s.patientId
                ? await getImageUrl('patient', s.patientId, abortController.current.signal)
                : null;
              const urlTerapeuta = s.therapistId
                ? await getImageUrl('therapist', s.therapistId, abortController.current.signal)
                : null;
              return { ...s, imagePaciente: urlPaciente, imageTerapeuta: urlTerapeuta };
            } catch (err) {
              if (err.name === 'AbortError') return { ...s, imagePaciente: null, imageTerapeuta: null };
              return { ...s, imagePaciente: null, imageTerapeuta: null };
            }
          })
        );

        // Garantir que eventos livres tenham appointmentType=0
        const eventosLivresMarcados = eventosLivresData.map(ev => ({
          ...ev,
          appointmentType: 0
        }));

        // Combinar sessões e eventos livres
        const data = [...sessoesComImagem, ...eventosLivresMarcados];

        // Salvar todos os atendimentos ordenados
        const ordenados = data.sort(
          (a, b) => new Date(a.startDatetime) - new Date(b.startDatetime)
        );

        setAtendimentosCompletos(ordenados);
        
        // Filtrar apenas atendimentos que estão acontecendo agora
        const atendimentosAgora = filtrarAtendimentosAgora(ordenados);
        setAtendimentos(atendimentosAgora);
      } catch (err) {
        // Don't show error if request was aborted
        if (err.name === 'AbortError') return;
        console.error("Erro ao carregar escala de sessões", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAtendimentos();

    // Atualizar filtro a cada minuto para refletir o horário atual
    const intervalId = setInterval(() => {
      setAtendimentosCompletos((prev) => {
        const atendimentosAgora = filtrarAtendimentosAgora(prev);
        setAtendimentos(atendimentosAgora);
        return prev;
      });
    }, 60000); // Atualiza a cada 1 minuto

    // Cleanup: abort the request when component unmounts
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
      clearInterval(intervalId);
    };
  }, []);

  const formatHorario = (inicio, fim) => {
    const start = new Date(inicio);
    const end = new Date(fim);
    return `${start.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })} às ${end.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const getFirstName = (nome) => nome?.split(" ")[0] || "";

  return (
    <div className="col-12 mb-4">
      <div className="card card-dashboard-20" style={{ border: '1px solid #dee2e6', borderRadius: '12px' }}>
        <div className="card-header pb-0">
          <div className="row">
            <div className="col-lg-6 col-7">
              <h6 className="mb-2 font-weight-bolder" style={{ marginBottom: '0.75rem' }}>Acontecendo agora</h6>
            </div>
          </div>
        </div>
        <div className="card-body px-3 pb-2" style={{ overflow: 'visible' }}>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : atendimentos.length === 0 ? (
            <div className="text-center py-3 text-sm">
              Nenhum atendimento acontecendo no momento
            </div>
          ) : (
            <div className="row g-2" style={{ overflow: 'visible' }}>
              {atendimentos.map((a) => {
                const isEventoLivre = a.appointmentType === 0;
                const isFalta = a.absenceType === 1 || a.absenceType === 2;
                const backgroundColor = isEventoLivre ? 'rgba(255, 165, 0, 0.1)' : 'transparent';
                const borderColor = isFalta ? 'red' : 'rgba(0, 0, 0, 0.08)';
                const borderWidth = isFalta ? '2px' : '1px';
                
                return (
                  <div key={a.id} className="col-md-6" style={{ overflow: 'visible' }}>
                    <div 
                      className="d-flex align-items-center p-2" 
                      style={{ 
                        minHeight: '55px', 
                        border: `${borderWidth} solid ${borderColor}`, 
                        borderRadius: '12px', 
                        overflow: 'hidden',
                        backgroundColor: backgroundColor
                      }}
                    >
                      <i
                        className="ni ni-time-alarm text-info text-gradient me-2"
                        style={{ fontSize: '0.9rem' }}
                      ></i>
                      <div className="me-2" style={{ minWidth: '70px' }}>
                        <p className="mb-0 text-xs text-muted" style={{ fontSize: '1.08rem', lineHeight: '1.2' }}>
                          {formatHorario(a.startDatetime, a.endDatetime)}
                        </p>
                      </div>
                      {isEventoLivre ? (
                        <div className="flex-grow-1">
                          <p className="mb-0 text-xs font-weight-bold" style={{ fontSize: '1.224rem' }}>
                            {a.title || 'Evento Livre'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="me-2">
                            <img
                              src={a.imagePaciente || require('../assets/img/perfilvazio.png')}
                              alt="foto-paciente"
                              className="avatar avatar-sm me-3"
                              style={{ borderRadius: "1.75em" }}
                            />
                          </div>
                          <div className="me-2" style={{ minWidth: '60px' }}>
                            <p className="mb-0 text-xs font-weight-bold" style={{ fontSize: '1.224rem' }}>
                              {getFirstName(a.patient?.name)}
                            </p>
                          </div>
                          <div className="ms-auto d-flex align-items-center">
                            <p className="mb-0 text-xs me-2" style={{ fontSize: '1.224rem' }}>
                              {getFirstName(a.therapist?.name)}
                            </p>
                            <img
                              src={a.imageTerapeuta || require('../assets/img/perfilvazio.png')}
                              alt="foto-terapeuta"
                              className="avatar avatar-sm"
                              style={{ borderRadius: "1.75em" }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalaSessao;
